import React, { useState } from "react";
import {
  Plus, FileText, Camera, MessageSquareWarning, Check, Zap, XCircle, ArrowRightLeft,
  Ban, StickyNote, Pencil, Loader2, Send, ExternalLink, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { formatDateTime } from "@/lib/format";
import { Amount } from "@/components/common/Amount";
import { StatusChip } from "@/components/common/StatusChip";
import { Section } from "@/features/workorders/Section";
import { VoiceScriptPlayer } from "@/components/common/VoiceScriptPlayer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const MSG_TONE = { entregado: "green", leido: "green", enviado: "blue", encolado: "gray",
  reintento: "amber", fallido: "red", cancelado: "gray" };

const ICONS = {
  create: FileText,
  update: Pencil,
  state_change: ArrowRightLeft,
  transition_rejected: Ban,
  delete: XCircle,
  evidencia: Camera,
  observacion: MessageSquareWarning,
  observacion_resuelta: Check,
  adicional_propuesto: Zap,
  adicional_autorizado: Check,
  adicional_rechazado: XCircle,
  nota: StickyNote,
  mensaje: Send,
};

// Marcador del hilo: neutral por defecto, con estado reservado sólo
// cuando el evento realmente lo amerita (rechazo, alerta, resolución).
const MARK = {
  transition_rejected: "bg-state-critical-soft text-state-critical",
  observacion: "bg-state-critical-soft text-state-critical",
  adicional_propuesto: "bg-state-warn-soft text-state-warn",
  adicional_autorizado: "bg-state-ok-soft text-state-ok",
  adicional_rechazado: "bg-state-critical-soft text-state-critical",
  observacion_resuelta: "bg-state-ok-soft text-state-ok",
};

export const TimelinePanel = ({ woId, timeline, notas, canNote, onDone }) => {
  const { t } = useT();
  const [texto, setTexto] = useState("");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  const toggleExpanded = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const addNote = async () => {
    if (texto.trim().length < 2) return;
    setBusy(true);
    try {
      await api.post(`/work-orders/${woId}/notes`, { texto: texto.trim() });
      setTexto("");
      onDone?.();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8">
      {canNote && (
        <Section title={t("wo.notes.title")} testId="wo-notes">
          <Textarea data-testid="wo-note-input" value={texto} onChange={(e) => setTexto(e.target.value)}
            rows={2} placeholder={t("wo.notes.ph")} />
          <Button size="sm" data-testid="wo-note-add" disabled={busy} onClick={addNote} className="mt-2 h-8 bg-ink text-paper hover:opacity-90">
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1 h-3.5 w-3.5" />}
            {t("wo.notes.add")}
          </Button>
          {!!notas?.length && (
            <ul className="mt-3 border-t border-line">
              {notas.map((n) => (
                <li key={n.id} className="border-b border-line py-2 last:border-0">
                  <p className="text-[12.5px] leading-snug text-ink">{n.texto}</p>
                  <p className="mt-0.5 text-[11px] text-ink-tertiary">
                    {n.created_by_nombre} · {formatDateTime(n.created_at)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}

      <Section title={t("wo.timeline.title")} testId="wo-timeline">
        {!timeline?.length ? (
          <p className="py-6 text-[12.5px] text-ink-tertiary">{t("wo.timeline.empty")}</p>
        ) : (
          <ol className="relative">
            <span className="absolute left-[11px] top-1 bottom-1 w-px bg-line" />
            {timeline.map((e) => {
              const Icon = ICONS[e.tipo] || FileText;
              return (
                <li key={e.id} data-testid={`wo-timeline-${e.tipo}`} className="relative flex gap-3 pb-4 last:pb-0">
                  <span className={`relative z-10 mt-[1px] flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full ring-2 ring-paper ${MARK[e.tipo] || "bg-surface-sunken text-ink-secondary"}`}>
                    <Icon className="h-3 w-3" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[12.5px] font-medium leading-snug text-ink">
                      {t(`wo.timeline.types.${e.tipo}`)}
                    </p>
                    {e.detalle && e.tipo === "mensaje" ? (
                      <div className="mt-0.5">
                        <p className={`break-words text-[12px] leading-snug text-ink-secondary ${expanded.has(e.id) ? "" : "line-clamp-3"}`}>
                          {e.detalle}
                        </p>
                        {e.detalle.length > 140 && (
                          <button type="button" data-testid={`timeline-toggle-${e.message_id}`} onClick={() => toggleExpanded(e.id)}
                            className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-brand hover:text-brand-hover">
                            {expanded.has(e.id) ? (<>{t("wo.timeline.see_less")}<ChevronUp className="h-3 w-3" /></>) : (<>{t("wo.timeline.see_more")}<ChevronDown className="h-3 w-3" /></>)}
                          </button>
                        )}
                        {e.link && (
                          <a href={e.link} target="_blank" rel="noreferrer" data-testid={`timeline-open-link-${e.message_id}`}
                            className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-brand hover:text-brand-hover">
                            <ExternalLink className="h-3 w-3" /> {t("wo.timeline.open_sub_link")}
                          </a>
                        )}
                      </div>
                    ) : e.detalle && (
                      <p className="mt-0.5 break-words text-[12px] leading-snug text-ink-secondary">{e.detalle}</p>
                    )}
                    {e.sub_cost_cents !== undefined && (
                      <p className="mt-0.5 flex items-center gap-1.5 text-[11.5px]">
                        <Amount cents={e.sub_cost_cents} className="text-[11.5px] text-ink-secondary" />
                        {e.client_price_cents ? (<><span className="text-ink-tertiary">→</span><Amount cents={e.client_price_cents} className="text-[11.5px] text-ink-secondary" /></>) : null}
                      </p>
                    )}
                    {e.tipo === "mensaje" && (
                      <span className="mt-1 inline-flex flex-wrap items-center gap-1.5">
                        <StatusChip tone={e.canal === "voz" ? "amber" : "blue"} label={t(`channels.${e.canal}`)} />
                        <StatusChip tone={MSG_TONE[e.estado_mensaje] || "gray"} testId={`timeline-msg-state-${e.message_id}`} label={t(`messages.states.${e.estado_mensaje}`)} />
                        {e.respuesta?.tecla && (
                          <span className="font-mono text-[11px] font-medium text-state-ok">
                            {t("messages.key_pressed", { k: e.respuesta.tecla })}
                          </span>
                        )}
                      </span>
                    )}
                    {e.tipo === "mensaje" && e.canal === "voz" && (
                      <span className="mt-1.5 block">
                        <VoiceScriptPlayer script={e.voice_script || e.detalle} label={t("messages.play_script")} testId={`timeline-play-${e.message_id}`} />
                      </span>
                    )}
                    {e.thumb_url && (
                      <img src={e.thumb_url} alt="" className="mt-1.5 h-14 w-14 border border-line object-cover" />
                    )}
                    <p className="mt-0.5 text-[11px] text-ink-tertiary">
                      {e.autor} · {formatDateTime(e.at)}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </Section>
    </div>
  );
};
